/**
 * Module Toggles Hook
 *
 * Reads and updates the organization's module toggle state.
 * Module toggles are stored as JSONB on the organizations table
 * in the `module_toggles` column.
 *
 * If no toggles are stored, falls back to tier-based defaults.
 */

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/useOrganization";
import { useOrgPinnedMutation, type OrgPinned } from "@/hooks/useOrgPinnedMutation";
import { useBillingStatus } from "@/hooks/usePlatformBilling";
import { logAudit } from "@/lib/audit";
import { toast } from "sonner";
import {
  tierMeetsMinimum,
  type SubscriptionTier,
} from "@/lib/tier-config";
import { CAPABILITIES } from "@/lib/capabilities/registry";

// ── Types ────────────────────────────────────────────────────

type ModuleToggleMap = Record<string, boolean>;

interface UseModuleTogglesReturn {
  /** Check if a specific module (or sub-feature) is enabled */
  isModuleEnabled: (module: string) => boolean;
  /** Get the full resolved toggle map (modules + sub-features) */
  toggles: Record<string, boolean>;
  /** Whether toggle data is still loading */
  isLoading: boolean;
  /** Update a single module/sub-feature toggle */
  setModuleToggle: (module: string, enabled: boolean) => void;
  /** Whether the current tier allows toggling a module (vs. locked by tier) */
  canToggle: (module: string) => boolean;
}

// ── Hook ─────────────────────────────────────────────────────

export function useModuleToggles(): UseModuleTogglesReturn {
  const { organizationId } = useOrganization();
  const { effectiveTier } = useBillingStatus();
  const queryClient = useQueryClient();
  const currentTier = (effectiveTier || "free") as SubscriptionTier;

  // Fetch stored toggles from the organizations table
  const { data: storedToggles, isLoading } = useQuery({
    queryKey: ["module-toggles", organizationId],
    queryFn: async () => {
      if (!organizationId) return null;

      const { data, error } = await supabase
        .from("organizations")
        .select("module_toggles")
        .eq("id", organizationId)
        .maybeSingle();

      if (error) {
        console.warn("[useModuleToggles] Failed to fetch toggles:", error.message);
        return null;
      }

      return (data?.module_toggles as ModuleToggleMap) ?? null;
    },
    enabled: !!organizationId,
    staleTime: 60_000,
  });

  // Resolve toggles from the capability registry: tier-based default → stored override.
  // Covers ALL toggleable capabilities (top-level modules + sub-features), not just
  // the legacy TOGGLEABLE_MODULES list.
  const toggles: Record<string, boolean> = {};
  for (const cap of Object.values(CAPABILITIES)) {
    if (!cap.toggle) continue;
    const qualifies = tierMeetsMinimum(currentTier, cap.tier ?? "free");
    toggles[cap.key] = qualifies && cap.toggle.default;
  }

  // Apply stored overrides (only honor a stored value when its tier qualifies)
  if (storedToggles) {
    for (const [k, v] of Object.entries(storedToggles)) {
      const cap = CAPABILITIES[k];
      if (cap?.toggle && tierMeetsMinimum(currentTier, cap.tier ?? "free")) {
        // ?? (not ||): a stored `false` is a real "off"; only null/undefined falls back to the registry default.
        toggles[k] = v ?? toggles[k];
      }
    }
  }

  const isModuleEnabled = (module: string): boolean => {
    return toggles[module] ?? false;
  };

  const canToggle = (module: string): boolean => {
    const tier = CAPABILITIES[module]?.tier ?? "free";
    return tierMeetsMinimum(currentTier, tier);
  };

  // Mutation to update a toggle. The church and the toggles being edited travel in the variables:
  // a write paused offline reads `mutationFn` only on resume, so neither may come from the render
  // closure or it would merge into, and overwrite, the church switched to (#7830).
  const { mutate: updateToggle } = useOrgPinnedMutation({
    mutationFn: async ({
      organizationId,
      module,
      enabled,
      baseToggles,
    }: OrgPinned<{ module: string; enabled: boolean; baseToggles: ModuleToggleMap | null | undefined }>) => {
      if (!organizationId) throw new Error("No organization selected");

      const newToggles = { ...baseToggles, [module]: enabled };

      const { error } = await supabase
        .from("organizations")
        .update({ module_toggles: newToggles } as any)
        .eq("id", organizationId);

      if (error) throw error;

      await logAudit({
        tableName: "organizations",
        recordId: organizationId,
        action: "update_module_toggle",
        organizationId,
        oldData: { module_toggles: baseToggles },
        newData: { module_toggles: newToggles },
        metadata: { module, enabled },
      });

      return newToggles;
    },
    onSuccess: (newToggles, { organizationId }) => {
      queryClient.setQueryData(["module-toggles", organizationId], newToggles);
    },
    onError: (error: Error) => {
      toast.error(`Failed to update module setting: ${error.message}`);
    },
  });

  const setModuleToggle = (module: string, enabled: boolean) => {
    updateToggle({ module, enabled, baseToggles: storedToggles });
  };

  return {
    isModuleEnabled,
    toggles,
    isLoading,
    setModuleToggle,
    canToggle,
  };
}
